import { useState, useCallback } from "react"
import { apiClient } from "../../../lib/api/client"
import type { GlobalAISession } from "../types"

interface UseGlobalAISessionReturn {
  sessions: GlobalAISession[]
  activeSession: GlobalAISession | null
  isLoading: boolean
  error: string | null
  createSession: (divisionId: string, name?: string) => Promise<GlobalAISession | null>
  loadSessions: () => Promise<void>
  loadSession: (sessionId: string) => Promise<void>
  archiveSession: (sessionId: string) => Promise<void>
  setActiveSession: (session: GlobalAISession | null) => void
  updateSessionName: (sessionId: string, name: string) => void
}

export function useGlobalAISession(): UseGlobalAISessionReturn {
  const [sessions, setSessions] = useState<GlobalAISession[]>([])
  const [activeSession, setActiveSession] = useState<GlobalAISession | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createSession = useCallback(async (divisionId: string, name?: string): Promise<GlobalAISession | null> => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.post("/v1/ai_agent/sessions", {
        session_type: "global",
        division_id: divisionId,
        name: name || "Global Assistant",
      })
      if (response.data.success) {
        const newSession = response.data.data
        setSessions((prev) => [newSession, ...prev])
        setActiveSession(newSession)
        return newSession
      } else {
        setError(response.data.message?.[0] || "Failed to create session")
        return null
      }
    } catch (err) {
      setError("Failed to create session")
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const loadSessions = useCallback(async (): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get("/v1/ai_agent/sessions/global")
      if (response.data.success) {
        const loadedSessions = response.data.data || []
        setSessions(loadedSessions)
        // Auto-select the most recent session if available
        if (loadedSessions.length > 0 && !activeSession) {
          setActiveSession(loadedSessions[0])
        }
      }
    } catch (err) {
      setError("Failed to load sessions")
    } finally {
      setIsLoading(false)
    }
  }, [activeSession])

  const loadSession = useCallback(async (sessionId: string): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient.get(`/v1/ai_agent/sessions/${sessionId}`)
      if (response.data.success) {
        setActiveSession(response.data.data)
      }
    } catch (err) {
      setError("Failed to load session")
    } finally {
      setIsLoading(false)
    }
  }, [])

  const archiveSession = useCallback(
    async (sessionId: string): Promise<void> => {
      try {
        await apiClient.delete(`/v1/ai_agent/sessions/${sessionId}`)
        setSessions((prev) => prev.filter((s) => s._id !== sessionId))
        if (activeSession?._id === sessionId) {
          setActiveSession(null)
        }
      } catch (err) {
        setError("Failed to archive session")
      }
    },
    [activeSession]
  )

  const updateSessionName = useCallback((sessionId: string, name: string): void => {
    // Only update sessions array (for dropdown display), not activeSession
    // This prevents side effects that could reset messages during streaming
    setSessions((prev) => prev.map((s) => (s._id === sessionId ? { ...s, name } : s)))
  }, [])

  return {
    sessions,
    activeSession,
    isLoading,
    error,
    createSession,
    loadSessions,
    loadSession,
    archiveSession,
    setActiveSession,
    updateSessionName,
  }
}
